import { AuthService } from './authService';
import {
    MoveProcess,
    CreateMoveProcessRequest,
    UpdateMoveProcessRequest,
    AddMoveProcessStepRequest,
    UpdateMoveProcessStepRequest,
    MoveProcessStep,
    ValidateMoveProcessResponse,
    ExecuteMoveProcessResponse,
    AddStepsBatchRequest,
    AddStepsBatchResponse
} from '../components/warehouse/modules/move-processes/moveProcessTypes';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

async function parseOrThrow<T>(response: Response, errorPrefix: string): Promise<T> {
    if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.errorMessage || data?.error || `${errorPrefix}: ${response.status}`);
    }
    // 204 No Content
    if (response.status === 204) return null as unknown as T;
    return await response.json();
}

class MoveProcessService {
    async list(warehouseId?: string, status?: number): Promise<MoveProcess[]> {
        const params = new URLSearchParams();
        if (warehouseId) params.append('warehouseId', warehouseId);
        if (status !== undefined) params.append('status', String(status));
        const url = `${API_BASE_URL}/move-processes${params.toString() ? `?${params}` : ''}`;
        const response = await AuthService.authenticatedGet(url);
        return parseOrThrow<MoveProcess[]>(response, 'Kunne ikke hente planer');
    }

    async getById(id: number): Promise<MoveProcess> {
        const response = await AuthService.authenticatedGet(`${API_BASE_URL}/move-processes/${id}`);
        return parseOrThrow<MoveProcess>(response, 'Kunne ikke hente plan');
    }

    async create(request: CreateMoveProcessRequest): Promise<MoveProcess> {
        const response = await AuthService.authenticatedPost(`${API_BASE_URL}/move-processes`, request);
        return parseOrThrow<MoveProcess>(response, 'Kunne ikke opprette plan');
    }

    async update(id: number, request: UpdateMoveProcessRequest): Promise<MoveProcess> {
        const response = await AuthService.authenticatedPut(`${API_BASE_URL}/move-processes/${id}`, request);
        return parseOrThrow<MoveProcess>(response, 'Kunne ikke oppdatere plan');
    }

    async delete(id: number): Promise<void> {
        const response = await AuthService.authenticatedDelete(`${API_BASE_URL}/move-processes/${id}`);
        await parseOrThrow(response, 'Kunne ikke slette plan');
    }

    async addStep(processId: number, request: AddMoveProcessStepRequest): Promise<MoveProcessStep> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/move-processes/${processId}/steps`, request);
        return parseOrThrow<MoveProcessStep>(response, 'Kunne ikke legge til steg');
    }

    async addStepsBatch(processId: number, request: AddStepsBatchRequest): Promise<AddStepsBatchResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/move-processes/${processId}/steps/batch`, request);
        return parseOrThrow<AddStepsBatchResponse>(response, 'Kunne ikke legge til steg samlet');
    }

    async updateStep(processId: number, stepId: number, request: UpdateMoveProcessStepRequest): Promise<MoveProcessStep> {
        const response = await AuthService.authenticatedPut(
            `${API_BASE_URL}/move-processes/${processId}/steps/${stepId}`, request);
        return parseOrThrow<MoveProcessStep>(response, 'Kunne ikke oppdatere steg');
    }

    async deleteStep(processId: number, stepId: number): Promise<void> {
        const response = await AuthService.authenticatedDelete(
            `${API_BASE_URL}/move-processes/${processId}/steps/${stepId}`);
        await parseOrThrow(response, 'Kunne ikke slette steg');
    }

    async validate(id: number): Promise<ValidateMoveProcessResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/move-processes/${id}/validate`, {});
        return parseOrThrow<ValidateMoveProcessResponse>(response, 'Kunne ikke validere plan');
    }

    async plan(id: number): Promise<ValidateMoveProcessResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/move-processes/${id}/plan`, {});
        return parseOrThrow<ValidateMoveProcessResponse>(response, 'Kunne ikke planlegge');
    }

    async execute(id: number): Promise<ExecuteMoveProcessResponse> {
        const response = await AuthService.authenticatedPost(
            `${API_BASE_URL}/move-processes/${id}/execute`, {});
        return parseOrThrow<ExecuteMoveProcessResponse>(response, 'Kunne ikke utføre plan');
    }
}

export const moveProcessService = new MoveProcessService();
